import type { Map as MLMap } from "maplibre-gl";

import { DEMOGRAPHICS_FILL, demographicsColor } from "./layers/demographics";
import { CATNAT_FILL, catnatColor } from "./layers/risks";
import { CLIMATE_FILL, HEAT_FILL, climateColor, heatColor } from "./layers/climate";
import { useDemographicsStore } from "../state/useDemographicsStore";
import { useRisksStore } from "../state/useRisksStore";
import { useClimateStore } from "../state/useClimateStore";

/**
 * Layers whose shading follows a pane's controls.
 *
 * Several layers carry every measure they can draw as separate attributes on
 * the same feature, so switching what is shown is a paint change on geometry
 * the browser already holds — never a refetch. Each one needs the same three
 * things: a subscription outside React's render loop, change detection so an
 * unrelated store write does not repaint, and the same call again at
 * `style.load` so a returning user's persisted choice lands before the first
 * frame rather than after it.
 *
 * Collected here rather than as one `useEffect` per layer in `MapView`, which
 * is what this replaced. Four copies of the same block was three too many, and
 * the map component should not have to learn about a new pane to draw its
 * layer.
 */
export interface Repainter {
  /** Store subscription. Returns its unsubscribe, as zustand's does. */
  subscribe: (listener: () => void) => () => void;
  /**
   * A string that changes exactly when the shading should. Cheaper and harder
   * to get wrong than comparing each field by hand.
   */
  key: () => string;
  apply: (map: MLMap) => void;
}

function paint(map: MLMap, layer: string, value: unknown): void {
  if (map.getLayer(layer)) {
    map.setPaintProperty(layer, "fill-color", value as never);
  }
}

export const REPAINTERS: Repainter[] = [
  {
    subscribe: (l) => useDemographicsStore.subscribe(l),
    key: () => {
      const s = useDemographicsStore.getState();
      return `${s.metric}:${s.period}`;
    },
    apply: (map) => {
      const s = useDemographicsStore.getState();
      paint(map, DEMOGRAPHICS_FILL, demographicsColor(s.metric, s.period));
    },
  },
  {
    subscribe: (l) => useRisksStore.subscribe(l),
    key: () => {
      const s = useRisksStore.getState();
      return `${s.metric}:${s.recentOnly}`;
    },
    apply: (map) => {
      const s = useRisksStore.getState();
      paint(map, CATNAT_FILL, catnatColor(s.metric, s.recentOnly));
    },
  },
  {
    subscribe: (l) => useClimateStore.subscribe(l),
    key: () => {
      const s = useClimateStore.getState();
      return `${s.metric}:${s.scenario}:${s.horizon}:${s.heatWhen}`;
    },
    apply: (map) => {
      const s = useClimateStore.getState();
      paint(map, CLIMATE_FILL, climateColor(s.metric, s.scenario, s.horizon));
      paint(map, HEAT_FILL, heatColor(s.heatWhen));
    },
  },
];

/** Apply every repainter once — used at `style.load`. */
export function applyRepainters(map: MLMap): void {
  for (const r of REPAINTERS) r.apply(map);
}
